import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, CheckCircle, Clock, X, AlertCircle, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : '-';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const STATUS_CONFIG = {
  pending_payment:      { label: 'Belum Bayar',       color: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-400', icon: Clock },
  pending_verification: { label: 'Bukti Diunggah',    color: 'bg-blue-100 text-blue-700',    border: 'border-blue-400',   icon: AlertCircle },
  confirmed:            { label: 'Lunas',              color: 'bg-green-100 text-green-700',  border: 'border-green-400',  icon: CheckCircle },
  cancelled:            { label: 'Dibatalkan',         color: 'bg-red-100 text-red-600',      border: 'border-red-300',    icon: X },
};

export default function Cleaning({ userRole, userPropertyName }) {
  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomsError, setRoomsError] = useState('');
  const [roomsRetry, setRoomsRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('aktif');
  const [selected, setSelected] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [showCancelInput, setShowCancelInput] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');

  // Form input pembersihan
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    property_name: userPropertyName || '',
    room_number: '',
    cleaning_date: new Date().toISOString().slice(0, 10),
    cleaning_time: '09:00',
    amount: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/cleaning-requests`, cfg());
      setRequests(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/staff/properties`, cfg());
      setProperties(res.data || []);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/cleaning-settings`, cfg());
      if (res.data?.cleaning_price) setDefaultPrice(String(res.data.cleaning_price));
    } catch {}
  };

  useEffect(() => { fetchRequests(); fetchProperties(); fetchSettings(); }, []);

  // Load rooms when form property changes
  useEffect(() => {
    let active = true;
    setRooms([]);
    setRoomsError('');
    setLoadingRooms(false);
    if (!showForm || !form.property_name) return;
    setLoadingRooms(true);
    axios.get(`${BACKEND_URL}/api/staff/rooms-with-occupancy?property_name=${encodeURIComponent(form.property_name)}`, cfg())
      .then(res => {
        if (active) setRooms((res.data || []).filter(room => room.status === 'occupied'));
      })
      .catch(() => {
        if (active) setRoomsError('Daftar kamar gagal dimuat. Silakan coba lagi.');
      })
      .finally(() => { if (active) setLoadingRooms(false); });
    return () => { active = false; };
  }, [form.property_name, showForm, roomsRetry]);

  // Auto-fill default price when form opens
  useEffect(() => {
    if (showForm && defaultPrice && !form.amount) {
      setForm(f => ({ ...f, amount: defaultPrice }));
    }
  }, [showForm]);

  const openDetail = async (req) => {
    setSelected(req);
    setCancelNote('');
    setShowCancelInput(false);
    setProofUrl(null);
    if (req.status === 'pending_verification' || req.status === 'confirmed') {
      setLoadingProof(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/cleaning-requests/${encodeURIComponent(req.id)}/proof`, cfg());
        setProofUrl(res.data.proof_image_url || null);
      } catch {} finally { setLoadingProof(false); }
    }
  };

  const handleConfirm = async () => {
    if (userRole !== 'superadmin') return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/cleaning-requests/${encodeURIComponent(selected.id)}/confirm`, {}, cfg());
      setSuccess('Pembayaran dikonfirmasi! Status berubah menjadi Lunas.');
      setSelected(null);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal konfirmasi'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Hapus record ini secara permanen?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/cleaning-requests/${encodeURIComponent(selected.id)}`, cfg());
      setSuccess('Record berhasil dihapus');
      setSelected(null);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menghapus'); }
  };

  const handleCancel = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/cleaning-requests/${encodeURIComponent(selected.id)}/cancel`, { admin_notes: cancelNote }, cfg());
      setSuccess('Request dibatalkan.');
      setSelected(null);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal membatalkan'); }
  };

  const handleSubmitForm = async () => {
    if (!form.property_name || !form.room_number || !form.cleaning_date || !form.cleaning_time) {
      setError('Gedung, kamar, tanggal, dan waktu wajib diisi'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/cleaning-requests`, form, cfg());
      setSuccess('Tagihan pembersihan berhasil dikirim ke tenant');
      setShowForm(false);
      setForm({ property_name: userPropertyName || '', room_number: '', cleaning_date: new Date().toISOString().slice(0, 10), cleaning_time: '09:00', amount: defaultPrice || '', notes: '' });
      setRooms([]);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSubmitting(false); }
  };

  const aktif = requests.filter(r => r.status === 'pending_payment' || r.status === 'pending_verification' || r.status === 'cancelled');
  const lunas = requests.filter(r => r.status === 'confirmed');
  const pending = aktif.filter(r => r.status === 'pending_payment');
  const needVerif = aktif.filter(r => r.status === 'pending_verification');
  const cancelled = aktif.filter(r => r.status === 'cancelled');
  const badgeVerif = needVerif.length;

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}<button className="ml-2 text-red-400" onClick={() => setError('')}><X className="w-4 h-4 inline" /></button></div>}
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" /> Pembersihan Kamar
        </h2>
        <Button onClick={() => { setShowForm(true); setForm(f => ({ ...f, amount: defaultPrice || '' })); }} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full text-sm">
          <Plus className="w-4 h-4 mr-1" /> Input Pembersihan
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit">
        <button onClick={() => setSubTab('aktif')}
          className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab === 'aktif' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <Clock className="w-4 h-4" /> Tagihan Aktif
          {badgeVerif > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{badgeVerif}</span>}
        </button>
        <button onClick={() => setSubTab('lunas')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${subTab === 'lunas' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <CheckCircle className="w-4 h-4 text-green-500" /> Lunas
          {lunas.length > 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{lunas.length}</span>}
        </button>
      </div>

      {/* Tab Tagihan Aktif */}
      {subTab === 'aktif' && (
        <div className="space-y-5">
          {pending.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-yellow-700 mb-2">Belum Bayar ({pending.length})</p>
              <div className="space-y-2">{pending.map(r => <RequestCard key={r.id} r={r} onClick={() => openDetail(r)} />)}</div>
            </div>
          )}
          {needVerif.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">Bukti Diunggah — Perlu Konfirmasi ({needVerif.length})</p>
              <div className="space-y-2">{needVerif.map(r => <RequestCard key={r.id} r={r} onClick={() => openDetail(r)} />)}</div>
            </div>
          )}
          {cancelled.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-isk-muted mb-2">Dibatalkan ({cancelled.length})</p>
              <div className="space-y-2">{cancelled.map(r => <RequestCard key={r.id} r={r} onClick={() => openDetail(r)} />)}</div>
            </div>
          )}
          {aktif.length === 0 && (
            <div className="text-center py-16 text-isk-muted">Tidak ada tagihan aktif</div>
          )}
        </div>
      )}

      {/* Tab Lunas */}
      {subTab === 'lunas' && (
        <div className="space-y-3">
          {lunas.length === 0 ? (
            <div className="text-center py-16 text-isk-muted">Belum ada pembersihan yang lunas</div>
          ) : (
            <div className="glass-card rounded-2xl overflow-auto max-h-[65vh]">
              <table className="w-full min-w-max text-sm">
                <thead>
                  <tr className="bg-white/60 border-b border-gray-100 sticky top-0 z-10">
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Tenant</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Gedung</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Kamar</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Tgl Pembersihan</th>
                    <th className="px-3 py-2.5 text-right font-medium text-isk-muted">Biaya</th>
                    <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Dikonfirmasi</th>
                  </tr>
                </thead>
                <tbody>
                  {lunas.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-white/40 cursor-pointer transition-colors" onClick={() => openDetail(r)}>
                      <td className="px-3 py-2.5 font-medium text-isk-text">{r.tenant_name || <span className="text-isk-muted italic">—</span>}</td>
                      <td className="px-3 py-2.5 text-isk-muted">{r.property_name}</td>
                      <td className="px-3 py-2.5 text-isk-text">{r.room_number}</td>
                      <td className="px-3 py-2.5 text-isk-muted whitespace-nowrap">{fmtDate(r.cleaning_date)} {r.cleaning_time}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-green-700">{fmt(r.amount)}</td>
                      <td className="px-3 py-2.5 text-xs text-isk-muted whitespace-nowrap">{fmtDateTime(r.confirmed_at)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-white/60 border-t border-gray-200">
                    <td colSpan={4} className="px-3 py-2.5 text-sm font-semibold text-isk-text">Total</td>
                    <td className="px-3 py-2.5 text-right font-bold text-green-700">{fmt(lunas.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Input Pembersihan */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="font-bold text-isk-text">Input Pembersihan Kamar</h3>
                <p className="text-xs text-isk-muted mt-0.5">Tagihan langsung dikirim ke tenant</p>
              </div>
              <button onClick={() => { setShowForm(false); setRooms([]); }}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>

            <div className="space-y-4">
              {!userPropertyName && (
                <div>
                  <Label htmlFor="cleaning-property" className="text-xs">Gedung</Label>
                  <select id="cleaning-property" value={form.property_name}
                    onChange={e => setForm(f => ({ ...f, property_name: e.target.value, room_number: '' }))}
                    className="w-full mt-1 border rounded-xl px-3 py-2 text-sm bg-white">
                    <option value="">Pilih gedung...</option>
                    {properties.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <Label htmlFor="cleaning-room" className="text-xs">Kamar</Label>
                {loadingRooms ? (
                  <div className="mt-1 border rounded-xl px-3 py-2 text-sm text-isk-muted bg-gray-50">Memuat kamar...</div>
                ) : (
                  <select id="cleaning-room" value={form.room_number}
                    onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))}
                    className="w-full mt-1 border rounded-xl px-3 py-2 text-sm bg-white"
                    disabled={!form.property_name}>
                    <option value="">Pilih kamar...</option>
                    {rooms.map(r => (
                      <option key={r.room_number} value={r.room_number}>
                        {r.room_number}{r.tenant_name ? ` — ${r.tenant_name}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {roomsError && (
                  <div role="alert" className="text-xs text-red-600 mt-1">
                    {roomsError}
                    <button type="button" className="ml-2 underline" onClick={() => setRoomsRetry(value => value + 1)}>Coba lagi</button>
                  </div>
                )}
                {form.property_name && !loadingRooms && !roomsError && rooms.length === 0 && (
                  <p className="text-xs text-isk-muted mt-1">Tidak ada kamar berpenghuni</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tanggal</Label>
                  <Input type="date" value={form.cleaning_date}
                    onChange={e => setForm(f => ({ ...f, cleaning_date: e.target.value }))}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs">Waktu</Label>
                  <Input type="time" value={form.cleaning_time}
                    onChange={e => setForm(f => ({ ...f, cleaning_time: e.target.value }))}
                    className="mt-1 rounded-xl" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Biaya (Rp)</Label>
                <Input type="number" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0" className="mt-1 rounded-xl" />
                {defaultPrice && <p className="text-xs text-isk-muted mt-1">Default: {fmt(defaultPrice)}</p>}
              </div>

              <div>
                <Label className="text-xs">Catatan <span className="text-gray-400">— opsional</span></Label>
                <Input value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Misal: termasuk pembersihan kamar mandi"
                  className="mt-1 rounded-xl" />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => { setShowForm(false); setRooms([]); }} className="flex-1 rounded-full">Batal</Button>
              <Button onClick={handleSubmitForm} disabled={submitting} className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full">
                {submitting ? 'Mengirim...' : 'Kirim Tagihan'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-isk-text">{selected.tenant_name || 'Tanpa Tenant'}</h3>
                <p className="text-xs text-isk-muted">{selected.property_name} · Kamar {selected.room_number}</p>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>

            <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 space-y-2 mb-4 text-sm">
              <div className="flex justify-between">
                <span className="text-isk-muted">Tanggal Pembersihan</span>
                <span className="font-semibold text-isk-text">{fmtDate(selected.cleaning_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-isk-muted">Waktu</span>
                <span className="font-semibold text-isk-text">{selected.cleaning_time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-isk-muted">Biaya</span>
                <span className="font-bold text-purple-700 text-lg">{fmt(selected.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-isk-muted">Status</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selected.status]?.color}`}>
                  {STATUS_CONFIG[selected.status]?.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-isk-muted">Dibuat</span>
                <span className="text-xs text-isk-muted">{fmtDateTime(selected.created_at)}</span>
              </div>
              {selected.admin_notes && (
                <div className="pt-2 border-t border-purple-100">
                  <p className="text-xs text-isk-muted">Catatan: <span className="text-isk-text">{selected.admin_notes}</span></p>
                </div>
              )}
            </div>

            {/* Bukti */}
            {(selected.status === 'pending_verification' || selected.status === 'confirmed') && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-isk-muted mb-2">Bukti Transfer</p>
                {loadingProof ? (
                  <div className="h-40 rounded-xl bg-gray-50 flex items-center justify-center text-isk-muted text-sm">Memuat foto...</div>
                ) : proofUrl ? (
                  <img src={proofUrl} alt="Bukti" className="w-full max-h-64 object-contain rounded-xl border border-gray-200 cursor-zoom-in"
                    onClick={() => setShowLightbox(true)} />
                ) : (
                  <div className="h-24 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-isk-muted text-sm">Tidak ada bukti pembayaran</div>
                )}
              </div>
            )}

            {/* Aksi */}
            {selected.status === 'pending_verification' && !showCancelInput && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCancelInput(true)}
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">
                  Batalkan
                </Button>
                {userRole === 'superadmin' && <Button onClick={handleConfirm} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full text-sm">
                  <CheckCircle className="w-4 h-4 mr-1" /> Konfirmasi Lunas
                </Button>}
              </div>
            )}
            {selected.status === 'pending_verification' && userRole !== 'superadmin' && (
              <p className="text-sm text-yellow-700">Menunggu verifikasi Super Admin</p>
            )}
            {selected.status === 'pending_payment' && !showCancelInput && (
              <Button variant="outline" onClick={() => setShowCancelInput(true)}
                className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">
                Batalkan Tagihan
              </Button>
            )}
            {showCancelInput && (
              <div className="space-y-2">
                <Label className="text-xs text-isk-muted">Alasan pembatalan (opsional)</Label>
                <Input value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Tulis alasan..." className="rounded-xl text-sm" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCancelInput(false)} className="flex-1 rounded-full text-sm">Batal</Button>
                  <Button onClick={handleCancel} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm">Konfirmasi Batalkan</Button>
                </div>
              </div>
            )}
            {selected.status === 'confirmed' && (
              <div className="space-y-2">
                <div className="p-3 rounded-xl bg-green-50 text-green-700 text-sm text-center font-medium">
                  ✓ Lunas · {fmtDateTime(selected.confirmed_at)}
                </div>
                <Button size="sm" variant="outline" onClick={handleDelete}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Record
                </Button>
              </div>
            )}
            {selected.status === 'cancelled' && (
              <div className="space-y-2">
                <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm text-center font-medium">✕ Tagihan dibatalkan</div>
                <Button size="sm" variant="outline" onClick={handleDelete}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Record
                </Button>
              </div>
            )}
            {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && proofUrl && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
          <img src={proofUrl} alt="Bukti" className="max-w-full max-h-full object-contain rounded-xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function RequestCard({ r, onClick }) {
  const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending_payment;
  const Icon = cfg.icon;
  return (
    <div className={`glass-card rounded-2xl p-4 border-l-4 cursor-pointer hover:shadow-md transition-shadow ${cfg.border}`} onClick={onClick}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-isk-text text-sm">{r.tenant_name || <span className="italic text-isk-muted">Tanpa tenant</span>}</p>
          <p className="text-xs text-isk-muted">{r.property_name} · Kamar {r.room_number}</p>
          <p className="text-xs text-isk-muted mt-0.5">
            {new Date(r.cleaning_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })} pukul {r.cleaning_time}
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}><Icon className="w-3 h-3 inline mr-0.5" />{cfg.label}</span>
          <span className="text-sm font-bold text-purple-700">{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(r.amount)}</span>
        </div>
      </div>
    </div>
  );
}
